import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card } from '@/components/ui/card';
import { Loader2, XCircle, ExternalLink, Trash2, Wand2 } from 'lucide-react';
import { MergeForm } from './MergeForm';
import { AutoMergeDialog } from './AutoMergeDialog';
import { DeleteContactDialog } from './DeleteContactDialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { captureError } from '@/lib/sentry';
import type { DuplicatePair, ContactDetails, EmailDetails, PhoneDetails, DuplicateWithDetails, AddressDetails } from '@/types/duplicate';

interface DuplicatesListProps {
  workspaceId: string;
  refreshTrigger?: number;
}

export function DuplicatesList({ workspaceId, refreshTrigger = 0 }: DuplicatesListProps) {
  const [duplicates, setDuplicates] = useState<DuplicateWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDuplicate, setSelectedDuplicate] = useState<DuplicateWithDetails | null>(null);
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [contactToDelete, setContactToDelete] = useState<ContactDetails | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAutoMerge, setShowAutoMerge] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchDuplicates = async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);

      // Get potential duplicates
      const { data: duplicatePairs, error: duplicatesError } = await supabase
        .rpc('get_potential_duplicates', { p_workspace_id: workspaceId });

      if (duplicatesError) throw duplicatesError;

      if (!duplicatePairs || duplicatePairs.length === 0) {
        setDuplicates([]);
        setLoading(false);
        return;
      }

      // Extract all unique contact IDs
      const contactIds = [...new Set(
        duplicatePairs.flatMap((d: DuplicatePair) => [d.contact1_id, d.contact2_id])
      )];

      // Use RPC to fetch all contact details in a single call (avoids URL length issues)
      // This sends IDs in POST body instead of GET query params
      const { data: batchData, error: batchError } = await supabase
        .rpc('get_duplicate_contacts_batch', {
          p_contact_ids: contactIds,
          p_workspace_id: workspaceId
        });

      if (batchError) throw batchError;

      // Build lookup maps from batch response
      const contactMap: Record<string, ContactDetails> = {};
      const emailsByContactId: Record<string, EmailDetails[]> = {};
      const phonesByContactId: Record<string, PhoneDetails[]> = {};
      const addressesByContactId: Record<string, AddressDetails[]> = {};

      (batchData || []).forEach((row: {
        contact_id: string;
        contact_type: string;
        first_name: string;
        last_name: string;
        company_name: string;
        created_at: string;
        emails: Array<{ email: string; label?: string; is_primary?: boolean }>;
        phones: Array<{ number: string; label?: string; is_primary?: boolean }>;
        addresses: Array<{ street?: string; city?: string; state?: string; zip?: string; is_primary?: boolean }>;
      }) => {
        // Map contact details
        contactMap[row.contact_id] = {
          id: row.contact_id,
          first_name: row.first_name || '',
          last_name: row.last_name || '',
          company_name: row.company_name || undefined,
          is_company: row.contact_type === 'company',
          workspace_id: workspaceId,
          type: row.contact_type || undefined,
          created_at: row.created_at || undefined,
        };

        // Map emails
        emailsByContactId[row.contact_id] = (row.emails || []).map((e: { email: string; label?: string; is_primary?: boolean }) => ({
          contact_id: row.contact_id,
          email: e.email,
          is_primary: e.is_primary ?? false,
        }));

        // Map phones
        phonesByContactId[row.contact_id] = (row.phones || []).map((p: { number: string; label?: string; is_primary?: boolean }) => ({
          contact_id: row.contact_id,
          phone_number: p.number,
          isPrimary: p.is_primary ?? false,
          label: p.label,
        }));

        // Map addresses
        addressesByContactId[row.contact_id] = (row.addresses || []).map((a: { id?: string; street?: string; street2?: string; city?: string; state?: string; zip?: string; country?: string; label?: string; is_primary?: boolean }, idx: number) => ({
          id: a.id || `${row.contact_id}-addr-${idx}`,
          contact_id: row.contact_id,
          address_line1: a.street || '',
          address_line2: a.street2,
          city: a.city || '',
          state: a.state,
          postal_code: a.zip,
          country: a.country || '',
          label: a.label,
          is_primary: a.is_primary ?? false,
        }));
      });

      // Combine all data
      const duplicatesWithDetails = (duplicatePairs || []).map((pair: DuplicatePair) => {
        return {
          duplicate: pair,
          contact1: contactMap[pair.contact1_id],
          contact2: contactMap[pair.contact2_id],
          emails1: emailsByContactId[pair.contact1_id] || [],
          emails2: emailsByContactId[pair.contact2_id] || [],
          phones1: phonesByContactId[pair.contact1_id] || [],
          phones2: phonesByContactId[pair.contact2_id] || [],
          addresses1: addressesByContactId[pair.contact1_id] || [],
          addresses2: addressesByContactId[pair.contact2_id] || [],
        };
      });

      setDuplicates(duplicatesWithDetails);
    } catch (error) {
      console.error('Error fetching duplicates:', error);

      captureError(error as Error, { feature: 'duplicates-fetch' });
      toast({
        title: "Error",
        description: "Failed to load potential duplicates. Please try again.",
        variant: "destructive",
      });

      setDuplicates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDuplicates();
  }, [workspaceId, refreshTrigger]);

  const handleMergeSuccess = (mergedContactId?: string) => {
    const mergedPair = selectedDuplicate;
    setSelectedDuplicate(null);

    // Optimistically remove the merged pair from UI immediately
    if (mergedPair) {
      setDuplicates(prev => prev.filter(d =>
        d.duplicate.contact1_id !== mergedPair.duplicate.contact1_id ||
        d.duplicate.contact2_id !== mergedPair.duplicate.contact2_id
      ));
    }

    // Also refresh from server after a short delay to catch any cascading changes
    setTimeout(() => fetchDuplicates(), 500);

    // Show success toast with link to merged contact
    if (mergedContactId) {
      toast({
        title: "Success",
        description: "Contacts merged successfully",
        action: (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/contacts/${mergedContactId}`)}
          >
            View Contact
          </Button>
        ),
      });
    } else {
      toast({
        title: "Success",
        description: "Contacts merged successfully",
      });
    }
  };

  const handleDismissDuplicate = async (duplicate: DuplicateWithDetails) => {
    const uniqueId = `${duplicate.duplicate.contact1_id}-${duplicate.duplicate.contact2_id}`;
    try {
      setDismissingId(uniqueId);
      
      // Make sure contact_id1 is lexicographically smaller, as required by the constraint
      const contact1 = duplicate.duplicate.contact1_id < duplicate.duplicate.contact2_id 
                      ? duplicate.duplicate.contact1_id 
                      : duplicate.duplicate.contact2_id;
      const contact2 = duplicate.duplicate.contact1_id < duplicate.duplicate.contact2_id 
                      ? duplicate.duplicate.contact2_id 
                      : duplicate.duplicate.contact1_id;
      
      // Insert directly into duplicate_decisions table following the documentation
      const { error } = await supabase
        .from('duplicate_decisions')
        .insert({
          contact_id1: contact1,
          contact_id2: contact2,
          decision: 'not_duplicate'
        });
      
      if (error) throw error;
      
      // Update UI by removing the dismissed pair
      setDuplicates(prevDuplicates => 
        prevDuplicates.filter(d => 
          d.duplicate.contact1_id !== duplicate.duplicate.contact1_id ||
          d.duplicate.contact2_id !== duplicate.duplicate.contact2_id
        )
      );
      
      toast({
        description: "Contacts marked as not duplicates",
      });
    } catch (error: any) {
      console.error("Error dismissing duplicate:", error);
      captureError(error as Error, { feature: 'duplicates-duplicates-list-handle-dismiss-duplicate' });
      toast({
        title: "Error",
        description: error?.message || "Failed to dismiss duplicate",
        variant: "destructive",
      });
    } finally {
      setDismissingId(null);
    }
  };

  const handleDeleteContact = (contact: ContactDetails) => {
    setContactToDelete(contact);
    setShowDeleteDialog(true);
  };

  const handleDeleteSuccess = () => {
    // Refresh duplicates list after successful deletion
    fetchDuplicates();
  };

  // Helper function to get field value safely
  const getFieldValue = (contact: any, field: string) => {
    return contact[field] || "—";
  };

  // Get initials for avatar
  const getContactInitials = useCallback((contact: ContactDetails): string => {
    if (contact.is_company) {
      return contact.company_name?.substring(0, 2).toUpperCase() || 'CO';
    }
    
    const firstInitial = contact.first_name?.charAt(0) || '';
    const lastInitial = contact.last_name?.charAt(0) || '';
    
    return (firstInitial + lastInitial).toUpperCase() || 'C';
  }, []);

  // Display loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading potential duplicates...</p>
        </div>
      </div>
    );
  }

  // Display error state
  if (duplicates.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <div className="space-y-2">
          <p>No potential duplicates found in this workspace.</p>
          <p className="text-sm">
            Duplicates are detected based on shared email addresses, phone numbers, or matching names.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {duplicates.length > 0 && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={() => setShowAutoMerge(true)}>
            <Wand2 className="h-4 w-4 mr-2" />
            Auto-Merge All
          </Button>
        </div>
      )}
      <div className="grid gap-6">
        {duplicates.map((duplicate) => {
          const duplicateId = `${duplicate.duplicate.contact1_id}-${duplicate.duplicate.contact2_id}`;
          const isDismissing = dismissingId === duplicateId;
          
          return (
            <Card key={duplicateId} className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Contact 1 */}
                <div className="space-y-4 border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar 
                        className="cursor-pointer h-10 w-10 hover:ring-2 hover:ring-primary transition-all"
                        onClick={() => navigate(`/contacts/${duplicate.contact1.id}`)}
                        title="View contact profile"
                      >
                        <AvatarFallback className="bg-primary/10 text-primary-foreground">
                          {getContactInitials(duplicate.contact1)}
                        </AvatarFallback>
                      </Avatar>
                      <h3 className="text-lg font-semibold">
                        {duplicate.contact1.is_company 
                          ? duplicate.contact1.company_name 
                          : `${duplicate.contact1.first_name} ${duplicate.contact1.last_name}`}
                      </h3>
                      {duplicate.contact1.is_company && (
                        <Badge className="ml-2">Company</Badge>
                      )}
                    </div>
                    <div className="flex">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteContact(duplicate.contact1)}
                        title="Delete contact"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => window.open(`/contacts/${duplicate.contact1.id}`, '_blank')}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>View full contact details</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                  
                  {/* Company or Job Information */}
                  {!duplicate.contact1.is_company && duplicate.contact1.company_name && (
                    <div className="text-sm">{duplicate.contact1.company_name}</div>
                  )}
                  {duplicate.contact1.job_title && (
                    <p className="text-sm text-muted-foreground">{duplicate.contact1.job_title}</p>
                  )}
                  {duplicate.contact1.industry && (
                    <p className="text-sm text-muted-foreground">Industry: {duplicate.contact1.industry}</p>
                  )}
                  
                  {/* Smart Fields */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <div>
                      <span className="text-muted-foreground">Type:</span>{" "}
                      {getFieldValue(duplicate.contact1, "type")}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Rank:</span>{" "}
                      {getFieldValue(duplicate.contact1, "rank")}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Status:</span>{" "}
                      {getFieldValue(duplicate.contact1, "status")}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Relationship:</span>{" "}
                      {getFieldValue(duplicate.contact1, "relationship")}
                    </div>
                  </div>
                </div>
                
                {/* Contact 2 */}
                <div className="space-y-4 border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar 
                        className="cursor-pointer h-10 w-10 hover:ring-2 hover:ring-primary transition-all"
                        onClick={() => navigate(`/contacts/${duplicate.contact2.id}`)}
                        title="View contact profile"
                      >
                        <AvatarFallback className="bg-primary/10 text-primary-foreground">
                          {getContactInitials(duplicate.contact2)}
                        </AvatarFallback>
                      </Avatar>
                      <h3 className="text-lg font-semibold">
                        {duplicate.contact2.is_company 
                          ? duplicate.contact2.company_name 
                          : `${duplicate.contact2.first_name} ${duplicate.contact2.last_name}`}
                      </h3>
                      {duplicate.contact2.is_company && (
                        <Badge className="ml-2">Company</Badge>
                      )}
                    </div>
                    <div className="flex">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteContact(duplicate.contact2)}
                        title="Delete contact"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => window.open(`/contacts/${duplicate.contact2.id}`, '_blank')}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>View full contact details</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                  
                  {/* Company or Job Information */}
                  {!duplicate.contact2.is_company && duplicate.contact2.company_name && (
                    <div className="text-sm">{duplicate.contact2.company_name}</div>
                  )}
                  {duplicate.contact2.job_title && (
                    <p className="text-sm text-muted-foreground">{duplicate.contact2.job_title}</p>
                  )}
                  {duplicate.contact2.industry && (
                    <p className="text-sm text-muted-foreground">Industry: {duplicate.contact2.industry}</p>
                  )}
                  
                  {/* Smart Fields */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <div>
                      <span className="text-muted-foreground">Type:</span>{" "}
                      {getFieldValue(duplicate.contact2, "type")}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Rank:</span>{" "}
                      {getFieldValue(duplicate.contact2, "rank")}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Status:</span>{" "}
                      {getFieldValue(duplicate.contact2, "status")}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Relationship:</span>{" "}
                      {getFieldValue(duplicate.contact2, "relationship")}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Match Type and Action */}
              <div className="mt-6 flex items-center justify-between border-t pt-4">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">
                    {duplicate.duplicate.match_type === 'shared_phone' ? 'Shared Phone' : 
                     duplicate.duplicate.match_type === 'exact_name_email' ? 'Exact Name & Email' : 
                     duplicate.duplicate.match_type}
                  </Badge>
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => handleDismissDuplicate(duplicate)}
                    disabled={isDismissing}
                  >
                    {isDismissing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Dismissing...
                      </>
                    ) : (
                      <>
                        <XCircle className="mr-2 h-4 w-4" />
                        Not Duplicates
                      </>
                    )}
                  </Button>
                  <Button onClick={() => setSelectedDuplicate(duplicate)}>
                    Merge Contacts
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      
      {selectedDuplicate && (
        <MergeForm
          duplicate={selectedDuplicate}
          onClose={() => setSelectedDuplicate(null)}
          onSuccess={handleMergeSuccess}
        />
      )}

      <DeleteContactDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        contact={contactToDelete}
        onSuccess={handleDeleteSuccess}
      />

      <AutoMergeDialog
        open={showAutoMerge}
        onOpenChange={setShowAutoMerge}
        workspaceId={workspaceId}
        duplicates={duplicates}
        onComplete={() => fetchDuplicates()}
      />
    </div>
  );
}
